import { useState } from "react";
import { Box, Collapse, Fade, IconButton, List, ListItem, Tooltip, Typography, useTheme } from "@mui/material";
import CustomIcon from "components/CustomIcon";

import icons from "enums/icons";
import { getAbilityIcon, getGroupColor } from "utils";
import { OverviewSubgroup } from "store/slice/Project/icDashboard/icDashboard.types";
import { ICBackgroundColours } from "constants/constants";

interface DataCardProps {
    data: OverviewSubgroup;
    isAllExpanded: boolean;
    showDefinitions: boolean;
}

const OverviewDataCard = ({ data, isAllExpanded, showDefinitions }: DataCardProps) => {
    const theme = useTheme();

    const [expandedSubGroups, setExpandedSubGroups] = useState<string[]>([]);
    const abilities = data.abilities.map((ability) => ability);

    const handleToggleExpand = (label: string) => {
        setExpandedSubGroups((prev) =>
            prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]
        );
    };

    return (
        <Box flex={1}>
            <Box
                sx={{
                    flex: "auto",
                    p: 1,
                    borderRadius: 2,
                    backgroundColor:
                        ICBackgroundColours[data?.colour as keyof typeof ICBackgroundColours] ??
                        ICBackgroundColours["RED"],
                    boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.2)",
                    height: 90,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="subtitle1" fontWeight={600}>
                        {data.subgroup_label}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} fontStyle="italic">
                        {data.subgroup_score}
                    </Typography>
                </Box>

                <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                    {data.subgroup_definition}
                </Typography>
            </Box>

            {abilities.map((ability, i) => (
                <Box
                    key={i}
                    sx={{
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        mt: 1,
                        backgroundColor: getGroupColor(ability.colour),
                    }}
                >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center">
                            <CustomIcon icon={getAbilityIcon(ability.colour)} />

                            <Tooltip
                                placement="top"
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            background: theme.palette.background.blue,
                                            borderRadius: "6px",
                                            color: theme.palette.common.black,
                                            boxShadow: "0 10px 20px 0 rgba(0, 0, 0, 0.15)",
                                        },
                                    },
                                }}
                                title={
                                    <Box>
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <CustomIcon
                                                icon={icons.circledQuestionMarkOutlined}
                                                sx={{ fontSize: 22, mr: 1 }}
                                            />
                                            <Typography variant="body1" fontSize={16} pb={0.5}>
                                                {ability.label}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption">{ability.ability_definition}</Typography>
                                    </Box>
                                }
                                TransitionComponent={Fade}
                                TransitionProps={{ timeout: 200 }}
                                enterDelay={400}
                            >
                                <Typography variant="body2" fontWeight={700} mx={1}>
                                    {ability.label}
                                </Typography>
                            </Tooltip>
                        </Box>

                        <IconButton onClick={() => handleToggleExpand(ability.label)} sx={{ padding: 0 }}>
                            <CustomIcon
                                type="outlined"
                                sx={{ fontSize: 16, color: theme.palette.primary.main }}
                                icon={
                                    expandedSubGroups.includes(ability.label) || isAllExpanded
                                        ? icons.chevronUpThin
                                        : icons.chevronDownThin
                                }
                            />
                        </IconButton>
                    </Box>

                    {showDefinitions && (
                        <Typography variant="caption" mt={2} display="block" sx={{ color: "#2E2E31" }}>
                            {ability.ability_definition}
                        </Typography>
                    )}

                    <Collapse in={expandedSubGroups.includes(ability.label) || isAllExpanded} unmountOnExit>
                        <List sx={{ marginTop: 2, paddingTop: 0, paddingBottom: 0 }}>
                            {ability.sub_abilities.map((subability, i) => (
                                <ListItem key={i} disablePadding sx={{ mb: "10px", "&:last-child": { mb: 0 } }}>
                                    <CustomIcon icon={getAbilityIcon(subability.colour)} />
                                    <Typography ml={1}>{subability.label}</Typography>
                                </ListItem>
                            ))}
                        </List>
                    </Collapse>
                </Box>
            ))}
        </Box>
    );
};

export default OverviewDataCard;
